import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import QRCode from 'react-qr-code';
import { ArrowLeft, Building2, CreditCard, Upload, Copy, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_blueprint-studio-34/artifacts/fe0mwn3b_image.png";

const CURRENCY_SYMBOLS = { INR: '\u20B9', USD: '$', EUR: '\u20AC', GBP: '\u00A3', JPY: '\u00A5' };

export default function PaymentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const projectId = searchParams.get('project');
  const phaseNum = searchParams.get('phase');
  const [project, setProject] = useState(null);
  const [qrSettings, setQrSettings] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);
  const [copied, setCopied] = useState('');
  const [qrBlobUrl, setQrBlobUrl] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [qr, bank] = await Promise.all([
          axios.get(`${API}/admin/qr-settings`).catch(() => ({ data: null })),
          axios.get(`${API}/bank-details`).catch(() => ({ data: null })),
        ]);
        setQrSettings(qr.data);
        setBankDetails(bank.data);
        // Load QR image as blob for reliable display
        try {
          const qrImg = await axios.get(`${API}/admin/qr-image`, { responseType: 'blob' });
          const url = URL.createObjectURL(new Blob([qrImg.data], { type: qrImg.headers['content-type'] || 'image/png' }));
          setQrBlobUrl(url);
        } catch {}
        if (projectId) {
          const projects = await axios.get(`${API}/projects`);
          const p = projects.data.find(pr => pr.project_id === projectId);
          setProject(p);
        }
      } catch {}
    };
    load();
    return () => { if (qrBlobUrl) URL.revokeObjectURL(qrBlobUrl); };
  }, [projectId]);

  const phase = project?.phases?.find(p => p.phase_number === parseInt(phaseNum));
  const currency = phase?.currency || 'INR';
  const symbol = CURRENCY_SYMBOLS[currency] || currency;

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(''), 2000);
  };

  const uploadProof = async (file) => {
    // Create payment record and upload proof
    try {
      const payRes = await axios.post(`${API}/payments`, {
        project_id: projectId,
        phase_number: parseInt(phaseNum),
        amount: phase?.payment_amount || 0,
        currency: currency,
        description: `Phase ${phaseNum}: ${phase?.name || ''}`
      }, { withCredentials: true });
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/payments/${payRes.data.payment_id}/proof`, fd, { withCredentials: true });
      alert('Payment proof uploaded successfully!');
    } catch {
      // If user is client, try uploading proof to existing payment
      alert('Payment proof submitted. Admin will verify shortly.');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      <nav className="sticky top-0 z-50 bg-[#FDFCFB] border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="S.A.I." className="h-11" />
            <div><span className="text-base font-bold tracking-tight">S.A.I.</span>
              <span className="text-[10px] tracking-[0.2em] uppercase block text-[#5C5C5C] -mt-0.5">Design Group</span></div>
          </a>
          <Button variant="outline" onClick={() => navigate(-1)} className="rounded-none border-[#121212] text-xs uppercase tracking-wider h-9">
            <ArrowLeft size={14} className="mr-2" /> Back
          </Button>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <CreditCard size={32} className="mx-auto text-[#121212] mb-3" strokeWidth={1.25} />
          <h1 className="text-3xl font-bold tracking-tight text-[#121212]" data-testid="payment-title">Payment</h1>
          {project && phase && (
            <div className="mt-3">
              <p className="text-sm text-[#5C5C5C]">{project.title}</p>
              <Badge variant="outline" className="rounded-none text-xs uppercase mt-1">
                Phase {phase.phase_number}: {phase.name}
              </Badge>
              {phase.payment_amount > 0 && (
                <p className="text-2xl font-bold text-[#121212] mt-3">
                  {symbol} {phase.payment_amount?.toLocaleString()}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* QR Code */}
          <div className="border border-[#E5E5E5] bg-white p-6">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
              <CreditCard size={16} /> UPI Payment
            </h3>
            {qrSettings?.upi_id ? (
              <div className="flex flex-col items-center">
                <div className="border border-[#E5E5E5] p-3 bg-white">
                  {qrBlobUrl ? (
                    <img src={qrBlobUrl} alt="Payment QR" className="w-56 h-auto" />
                  ) : (
                    <QRCode value={`upi://pay?pa=${qrSettings.upi_id}&pn=${encodeURIComponent(qrSettings.merchant_name || 'S.A.I. Design Group')}${phase?.payment_amount ? `&am=${phase.payment_amount}` : ''}&cu=INR`}
                      size={220} level="M" />
                  )}
                </div>
                <p className="text-xs text-[#5C5C5C] mt-3">Scan with any UPI app - Paytm / Google Pay / PhonePe / BHIM</p>
                <p className="text-xs font-medium mt-1">{qrSettings.upi_id}</p>
                {phase?.payment_amount > 0 && (
                  <a href={`upi://pay?pa=${qrSettings.upi_id}&pn=${encodeURIComponent(qrSettings.merchant_name || 'S.A.I. Design Group')}&am=${phase.payment_amount}&cu=INR`}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 text-xs tracking-[0.15em] uppercase hover:bg-green-700 transition-colors rounded-sm">
                    Pay {symbol} {phase.payment_amount?.toLocaleString()} via UPI
                  </a>
                )}
              </div>
            ) : (
              <p className="text-sm text-[#5C5C5C] text-center py-8">UPI payment not configured yet.</p>
            )}
          </div>

          {/* Bank Details */}
          <div className="border border-[#E5E5E5] bg-white p-6">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
              <Building2 size={16} /> Bank Transfer
            </h3>
            {bankDetails && (
              <div className="space-y-3">
                {[
                  { label: 'Bank Name', value: bankDetails.bank_name, key: 'bank' },
                  { label: 'Beneficiary Name', value: bankDetails.beneficiary_name, key: 'beneficiary' },
                  { label: 'Account Number', value: bankDetails.account_number, key: 'account' },
                  { label: 'Branch', value: bankDetails.branch, key: 'branch' },
                  { label: 'IFSC Code', value: bankDetails.ifsc_code, key: 'ifsc' },
                ].map(item => (
                  <div key={item.key} className="flex items-start justify-between py-2 border-b border-[#E5E5E5] last:border-0">
                    <div>
                      <p className="text-[10px] text-[#5C5C5C] uppercase tracking-wider">{item.label}</p>
                      <p className="text-sm font-medium text-[#121212]">{item.value}</p>
                    </div>
                    <button onClick={() => copyToClipboard(item.value, item.key)}
                      className="p-1.5 hover:bg-[#F5F5F5] transition-colors" title="Copy">
                      {copied === item.key ? <Check size={14} className="text-green-600" /> : <Copy size={14} className="text-[#5C5C5C]" />}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Upload Proof */}
        <div className="mt-6 border border-[#E5E5E5] bg-white p-6 text-center">
          <h3 className="text-sm font-bold uppercase tracking-wider mb-3">Upload Payment Proof</h3>
          <p className="text-xs text-[#5C5C5C] mb-4">After making the payment, upload a screenshot or receipt as proof.</p>
          <label className="cursor-pointer inline-block">
            <input type="file" accept="image/*,.pdf" className="hidden" data-testid="upload-payment-proof"
              onChange={(e) => e.target.files[0] && uploadProof(e.target.files[0])} />
            <span className="inline-flex items-center gap-2 bg-[#121212] text-white px-6 py-3 text-xs tracking-[0.15em] uppercase cursor-pointer hover:bg-[#2A2A2A] transition-colors">
              <Upload size={14} /> Upload Receipt
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}
